import { Injectable } from '@angular/core';
import { HttpHeaders, HttpResponse } from '@angular/common/http';

import { ServiceResponse } from "@common/data-models/service/service-response";
import { FilterDef } from "@common/data-models/navigation/filter-def";
import { ObjectTypes } from "@common/enums/object-types";
import { ViewStateFilter } from "@common/data-models/navigation/viewstate-filter";
import { ViewStateRecordsAlias } from "@common/services/layout-state";
import { PSTFieldNames } from "@common/data-models/fields/pst-field-names";

import { ViewStateOptions } from "@suite/common/data-models/navigation/viewstate-options";

import { NomWebServicesJson } from "@common/services/nom-web-services-json";
import { GlobalTablesService } from "@common/services/global-tables-service";
import { VisualStateService } from "@common/services/visual-state-service";
import { ZFields } from "@common/data-models/fields/z-fields";

import { PlatformConvertor } from "@common/services/platform-convertor";
import { DiscoverSearchResults } from "@discover/data-models/discover-search-results/discover-search-results";
import { RecordFactory } from '@src/suite/common/data-models/records/record-factory';
import { ApWireRecord } from '../data-models/records/ap-wire-record';
import { RssWireRecord } from '../data-models/records/rss-wire-record';
import { TwitterWireRecord } from '../data-models/records/twitter-wire-record';
import { WireRecord } from '../data-models/records/wire-record';
import { RSSFeedViewStateRecord } from '@src/suite/common/data-models/records/rss-feed-viewstate-record';
import { SingleFeedViewStateRecord } from '@common/data-models/records/single-feed-viewstate-record';

@Injectable({ providedIn: 'root' })
export class DiscoverSearchService {
    constructor(
        public nomWebServicesJson: NomWebServicesJson,
        public globalTablesService: GlobalTablesService,
        public visualStateService: VisualStateService,
        public platformConvertor: PlatformConvertor
    ) { }

    public async getSearchResults(vs: ViewStateRecordsAlias, guid?: string, poller?: boolean): Promise<ServiceResponse<DiscoverSearchResults>> {
        let results = new DiscoverSearchResults();
        let response: HttpResponse<any> = null;
        if (!poller) poller = false;

        let feedURL: string = "https://usv-qa.enpscloud.com/discover/wires/300/wire-types/-1";
        if (vs instanceof SingleFeedViewStateRecord) {
            let feedGUID = (<SingleFeedViewStateRecord>vs).viewStateFeedID;
            if (feedGUID) {
                feedURL = "https://usv-qa.enpscloud.com/discover/wires/300/feeds/" + feedGUID;
            }
        }

        try {
            //await this.nomWebServicesJson.discoverAPILogin();

            response = await this.nomWebServicesJson.discoverAPIGet(
                feedURL,
                {
                    headers: new HttpHeaders({
                        "Content-Type": "application/json",
                        //"Authorization": this.nomWebServicesJson.discoverToken,
                        "Authorization": this.nomWebServicesJson.token,
                        "accept": "text/plain"
                    }),
                    responseType: 'json',
                    observe: 'response'
                });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal({
                message: "Failed to load search results!",
                detailMessage: `Failed to load search results! \n\rStackTrace: NomWebServicesJson.getSearchResults(); \n\rError: ${error}`
            });
            return new ServiceResponse<DiscoverSearchResults>(false, results);
        }
        if (response.status) {
            let respObj = response.body;
            if (respObj) {
                for (let p of respObj) {
                    let wireRecord: WireRecord = null;
                    if (p.wireType == 1) {
                        wireRecord = RecordFactory.createRecordForType<ApWireRecord>(ObjectTypes.WIRE);
                    } else if (p.wireType == 2) {
                        wireRecord = RecordFactory.createRecordForType<TwitterWireRecord>(ObjectTypes.WIRE);
                    } else {
                        wireRecord = RecordFactory.createRecordForType<RssWireRecord>(ObjectTypes.WIRE);
                    }
                    wireRecord.data = p;
                    if (wireRecord.data) {
                        wireRecord.data = { ...wireRecord.data, ...wireRecord.data.apWire, ...wireRecord.data.twitterWire, ...wireRecord.data.rssWire };
                        delete wireRecord.data.apWire, delete wireRecord.data.twitterWire, delete wireRecord.data.rssWire;
                        results.addWire(wireRecord);
                    }
                }
            }
        }
        return new ServiceResponse<DiscoverSearchResults>(true, results);
    }

    // private getObjectTypeByViewStateType(vsType: ViewStateTypes, guid?: string) {
    //     if (vsType == ViewStateTypes.WIRE
    //         || vsType == ViewStateTypes.WIRESEARCH) {
    //         return ObjectTypes.WIRE;
    //     }
    //     if (guid) {
    //         return ObjectTypes.UNASSIGNED;
    //     }
    //     return null;
    // }

    protected buildFilterServiceParams(filter: ViewStateFilter, applyPrivs: boolean, sortBy: any, sortOrder: string, objectType?: number, search?: string, poller?: boolean, guid?: string): any {
        let QueryTerms: string = "";
        if (search) {
            QueryTerms = search;
        }

        filter = (filter) ? filter.deepCopy() : new ViewStateFilter();

        let zFields = new ZFields(!this.platformConvertor.useFor["search"]);
        zFields.add(
            "virtualPath",
            "\\\\NWS"
        );

        // don't want wires with no text and title
        zFields.add(
            "Title",
            "['' TO *]"
        );
        zFields.add(
            "text",
            "['' TO *]"
        );

        // if (poller && DiscoverSearchResults.latestModTime) {
        //     let fromDT = DiscoverSearchResults.latestModTime.moment.toDate().toISOString();
        //     let toDT = (new Date(3000, 0, 1)).toISOString();
        // zFields.add(
        //     "modTime__range__",
        //     fromDT + ";" + toDT
        // );
        // }

        //if (applyPrivs) {
        //    if (guid) {
        //        let type = this.checkForTypePrivs(ObjectTypes.UNASSIGNED)
        //        zFields.add("type__list__", type);
        //    } else {
        //        if (objectType) {
        //            let type = this.checkForTypePrivs(objectType)
        //            zFields.add("type", type);
        //        } else {
        //            let type = this.checkForTypePrivs(ObjectTypes.UNASSIGNED)
        //            zFields.add("type__list__", type);
        //        }
        //    }
        //} else {
        //    if (objectType) {
        //        zFields.add("type", objectType);
        //    }
        //}


        let queryTermTags: Array<string> = [];
        let queryTermUsers: Array<string> = [];

        let queryTermWords: Array<string> = [];

        let queryTermsArr = [];

        if (QueryTerms.startsWith("#")) {
            queryTermTags.push(QueryTerms.substr(1));
        } else { //we should support search for tags which consists of more than one word 
            queryTermsArr = QueryTerms.split(" ");
        }
        for (let term of queryTermsArr) {
            if (term.startsWith("@")) {
                queryTermUsers.push(term.substr(1));
            } else {
                queryTermWords.push(term);
            }
        }

        if (queryTermTags.length > 0) {
            let values: Array<string> = [];
            if (filter.map[PSTFieldNames.PSTTopicTag] && filter.map[PSTFieldNames.PSTTopicTag].Value) {
                for (let v of (<Array<string>>filter.map[PSTFieldNames.PSTTopicTag].Value)) {
                    if (values.indexOf(v) < 0) {
                        values.push(v);
                    }
                }
            }
            if (queryTermTags.length > 0) {
                for (let termTag of queryTermTags) {
                    if (values.indexOf(termTag) < 0) {
                        values.push(termTag);
                    }
                }
            }
            if (values.length > 0) {
                filter.filters.push(FilterDef.create(PSTFieldNames.PSTTopicTag, "LOOKUPLIST", values));
            }
        }


        if (queryTermUsers.length > 0) {
            for (let field of this.globalTablesService.FieldDefs) {
                let values: Array<string> = [];
                let isStaffIDList = this.globalTablesService.isStaffIDListFieldType(field.ID);
                if (isStaffIDList) {
                    if (queryTermUsers.length > 0) {
                        for (let termUser of queryTermUsers) {
                            let staffIDFilter = new FilterDef();
                            filter.groups.push(FilterDef.create(field.ID, "STAFFIDLIST", termUser));
                        }
                    }
                }
            }
        }
        this.fillZFieldsFromFilter(zFields, filter, guid);
        let sortFields = []
        if (!guid) {
            sortFields = this.buildSortFields(objectType, sortBy, sortOrder);
        }

        let maxResults = 200;

        return ({
            Database: this.nomWebServicesJson.PSTServer,
            QueryTerms: (queryTermsArr.length == 0) ? "" : queryTermsArr.join(" "),
            zFields: zFields.data,
            //NOMLocations: [{
            //    BasePath: "P_" + this.nomWebServicesJson.PSTGroup + "\\",
            //    SearchWIP: true,
            //    SearchArchives: false,
            //    SearchTrash: false
            //}],
            NOMContentTypes: {
                All: true,
                Audio: false,
                Contacts: false,
                Grids: false,
                Messages: false,
                NewsgatheringGrids: false,
                NewsgatheringItems: false,
                Pictures: false,
                Readins: false,
                Rundowns: false,
                Scripts: false,
                Storyboards: false,
                Video: false
            },
            NOMContentDates: {
                All: true,
                CustomDateRange: false,
                FortyEightHours: false,
                Future: false,
                ItemDateFrom: "1800-02-28T22:00:00.000Z",
                ItemDateTo: "1800-02-28T22:00:00.000Z",
                OneHour: false,
                OneWeek: false,
                TwentyFourHours: false,
                TwoHours: false
            },
            WireSearchParameters: {
                SearchWires: true,
                Categories: [],
                Providers: [],
                DateOptions: {
                    All: true,
                    CustomDateRange: false,
                    FortyEightHours: false,
                    Future: false,
                    ItemDateFrom: "1800-02-28T22:00:00.000Z",
                    ItemDateTo: "1800-02-28T22:00:00.000Z",
                    OneHour: false,
                    OneWeek: false,
                    TwentyFourHours: false,
                    TwoHours: false
                },
                ExtendedWireBriefing: true
            },
            MaxRows: maxResults,
            SortByRank: false,
            SortFields: sortFields,
            ExactMatch: false
        });
    }

    protected buildSortFields(objectType: any, sortByObj: ViewStateOptions, sortOrder: string): Array<{ [id: string]: string }> {
        let sortBy = sortByObj.Value;
        if (sortBy == PSTFieldNames.PSTModTime) sortBy = "modTime";
        return [{ FieldName: sortBy, FieldValue: sortOrder }, { FieldName: 'guid', FieldValue: "asc" }]
    }

    protected fillZFieldsFromFilter(zFields: ZFields, filterObj: ViewStateFilter, guid?: string) {
        if (filterObj && !guid) {
            for (let group of filterObj.groups) {
                let value = group.group();
                if (value) {
                    zFields.add(value.FieldName, value.FieldValue);
                }
            }

            for (let filter of filterObj.filters) {
                let value = filter.get();
                if (value) {
                    zFields.add(value.FieldName, value.FieldValue);
                }
            }
        }
        if (guid) {
            zFields.add("guid", guid);
        }

        return zFields;
    }
}
